import type { CsvFormatGuideDefinition } from 'components/CsvFormatGuide'

const hourHeaders = Array.from({ length: 24 }, (_, hour) => String(hour)).join(';')
const hourlyValues = Array.from({ length: 24 }, () => '100.00').join(';')

export const individualPricingCsvGuide: CsvFormatGuideDefinition = {
  title: 'Format indywidualnej ścieżki cenowej',
  layouts: [
    'Każdy wiersz ma dokładnie 25 kolumn: okres lub data oraz 24 ceny dla godzin 0–23. Tekst nagłówków nie ma znaczenia.',
    'Układ miesięczny zawiera 12 miesięcy. Układ dzienny zawiera 365 dat lub 366 dat z 29.02.'
  ],
  rules: [
    'Separator jest wykrywany automatycznie: średnik albo przecinek, jednak musi być jednakowy w całym pliku.',
    'Przy średniku ceny mogą używać przecinka lub kropki dziesiętnej. Przy przecinku wymagane są kropki dziesiętne.',
    'Dozwolone są ceny dodatnie, zerowe i ujemne.',
    'Daty dzienne: yyyy-MM-dd, dd.MM.yyyy, dd.MM lub angielski format Excela M/d/yyyy. Jeden format na plik.'
  ],
  examples: [
    {
      label: 'Pobierz przykład miesięczny',
      fileName: 'cennik-miesieczny.csv',
      csv: [`okres;${hourHeaders}`, ...Array.from({ length: 12 }, (_, month) => `${month + 1};${hourlyValues}`)].join('\n')
    },
    {
      label: 'Pobierz przykład dzienny',
      fileName: 'cennik-dzienny.csv',
      csv: [`data;${hourHeaders}`, ...calendarDates().map(date => `${date};${hourlyValues}`)].join('\n')
    }
  ]
}

export const energyProfileCsvGuide: CsvFormatGuideDefinition = {
  title: 'Format profilu energii',
  layouts: [
    'Macierz: pierwszy wiersz jest nagłówkiem, pierwsza kolumna zawiera datę, a kolejne kolumny wartości godzinowe.',
    'Dwie kolumny: znacznik daty i czasu oraz wartość energii. Dostępne są odczyty godzinowe i 15-minutowe.'
  ],
  rules: [
    'Wybierz układ zgodny z zawartością pliku przed wysłaniem.',
    'Plik musi mieć rozszerzenie .csv i zawierać komplet wymaganych okresów.',
    'Profil 15-minutowy musi mieć odczyty o HH:00, HH:15, HH:30 i HH:45 dla każdej godziny; ich średnia arytmetyczna tworzy wartość godzinową.',
    'Daty i wartości muszą zachowywać jeden format w całym pliku.'
  ],
  examples: [
    {
      label: 'Pobierz przykład macierzy',
      fileName: 'profil-macierz.csv',
      csv: profileMatrixExample()
    },
    {
      label: 'Pobierz przykład dwóch kolumn',
      fileName: 'profil-dwie-kolumny.csv',
      csv: profileTwoColumnExample()
    },
    {
      label: 'Pobierz przykład 15-minutowy',
      fileName: 'profil-15-minutowy.csv',
      csv: profileQuarterHourExample()
    }
  ]
}

function profileQuarterHourExample(): string {
  const rows = ['data;energia']
  calendarDates().forEach(date => {
    for (let hour = 0; hour < 24; hour += 1) {
      for (const minute of ['00', '15', '30', '45']) {
        rows.push(`2025-${date.slice(3, 5)}-${date.slice(0, 2)} ${String(hour).padStart(2, '0')}:${minute};1.000`)
      }
    }
  })
  return rows.join('\n')
}

function profileMatrixExample(): string {
  return [`data;${hourHeaders}`, ...calendarDates().map(date =>
    `2025-${date.slice(3, 5)}-${date.slice(0, 2)} 00:00;${Array.from({ length: 24 }, () => '1.000').join(';')}`
  )].join('\n')
}

function profileTwoColumnExample(): string {
  const rows = ['data;energia']
  calendarDates().forEach(date => {
    for (let hour = 0; hour < 24; hour += 1) {
      rows.push(`2025-${date.slice(3, 5)}-${date.slice(0, 2)} ${String(hour).padStart(2, '0')}:00;1.000`)
    }
  })
  return rows.join('\n')
}

function calendarDates(): string[] {
  const result: string[] = []
  const monthLengths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  monthLengths.forEach((length, monthIndex) => {
    for (let day = 1; day <= length; day += 1) {
      result.push(`${String(day).padStart(2, '0')}.${String(monthIndex + 1).padStart(2, '0')}`)
    }
  })
  return result
}
